import axios from 'axios';
import config from '../config/config';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: config.API_BASE_URL,
      timeout: config.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // If sending FormData, remove Content-Type header so axios sets it automatically with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response.data;
      },
      (error) => {
        if (error.response) {
          // Handle 401 Unauthorized
          if (error.response.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            window.location.href = '/login';
          }
          return Promise.reject(error.response.data);
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(credentials) {
    return this.api.post(config.endpoints.LOGIN, credentials);
  }

  async logout() {
    return this.api.post(config.endpoints.LOGOUT);
  }

  // Dashboard
  async getDashboardStats() {
    return this.api.get(config.endpoints.DASHBOARD_STATS);
  }

  // Rides
  async getRides(params) {
    return this.api.get(config.endpoints.RIDES_LIST, { params });
  }

  async getRideDetails(id) {
    return this.api.get(config.endpoints.RIDE_DETAILS.replace(':id', id));
  }

  async deleteRide(id) {
    return this.api.delete(`/admin/rides/${id}`);
  }

  // Drivers
  async getDrivers(params) {
    return this.api.get(config.endpoints.DRIVERS_LIST, { params });
  }

  async getDriverDetails(id) {
    return this.api.get(config.endpoints.DRIVER_DETAILS.replace(':id', id));
  }

  async addDriver(data) {
    return this.api.post(config.endpoints.DRIVER_ADD, data);
  }

  async updateDriver(id, data) {
    return this.api.put(config.endpoints.DRIVER_UPDATE.replace(':id', id), data);
  }

  async verifyDriverDocument(id, documentType, status, remarks) {
    return this.api.post(config.endpoints.DRIVER_VERIFY.replace(':id', id), {
      documentType,
      status,
      remarks,
    });
  }

  async verifyDriver(id) {
    return this.api.post(config.endpoints.DRIVER_VERIFY_STATUS.replace(':id', id));
  }

  async blockDriver(id, reason) {
    return this.api.post(config.endpoints.DRIVER_BLOCK.replace(':id', id), { reason });
  }

  async deleteDriver(id) {
    return this.api.delete(config.endpoints.DRIVER_DELETE.replace(':id', id));
  }

  async uploadDriverDocument(driverId, formData) {
    return this.api.post(config.endpoints.DRIVER_UPLOAD_DOCUMENT.replace(':id', driverId), formData);
  }

  async deleteDriverDocument(driverId, documentId) {
    return this.api.delete(config.endpoints.DRIVER_DELETE_DOCUMENT.replace(':id', driverId).replace(':documentId', documentId));
  }

  // Driver Recharge
  async getDriverRechargeStatus(driverId) {
    return this.api.get(`/admin/drivers/${driverId}/recharge-status`);
  }

  async getAdminRechargePlans(driverId = null) {
    const params = driverId ? { driverId } : {};
    return this.api.get('/admin/recharge-plans', { params });
  }

  async assignDriverRecharge(driverId, data) {
    return this.api.post(`/admin/drivers/${driverId}/assign-recharge`, data);
  }

  // Passengers
  async getPassengers(params) {
    return this.api.get(config.endpoints.PASSENGERS_LIST, { params });
  }

  async getPassengerDetails(id) {
    return this.api.get(config.endpoints.PASSENGER_DETAILS.replace(':id', id));
  }

  async addPassenger(data) {
    return this.api.post(config.endpoints.PASSENGER_ADD, data);
  }

  async updatePassenger(id, data) {
    return this.api.put(config.endpoints.PASSENGER_UPDATE.replace(':id', id), data);
  }

  async deletePassenger(id) {
    return this.api.delete(config.endpoints.PASSENGER_DELETE.replace(':id', id));
  }

  // Verifications
  async getPendingVerifications() {
    return this.api.get(config.endpoints.PENDING_VERIFICATIONS);
  }

  // Transactions
  async getTransactions(params) {
    return this.api.get(config.endpoints.TRANSACTIONS_LIST, { params });
  }

  async getTransactionDetails(id) {
    return this.api.get(config.endpoints.TRANSACTION_DETAILS.replace(':id', id));
  }

  // Withdrawals
  async getWithdrawals(params) {
    return this.api.get(config.endpoints.WITHDRAWALS_LIST, { params });
  }

  async approveWithdrawal(id, data) {
    return this.api.post(config.endpoints.WITHDRAWAL_APPROVE.replace(':id', id), data);
  }

  // Promo Codes
  async getPromoCodes(params) {
    return this.api.get(config.endpoints.PROMOS_LIST, { params });
  }

  async addPromoCode(data) {
    return this.api.post(config.endpoints.PROMO_ADD, data);
  }

  async updatePromoCode(id, data) {
    return this.api.put(config.endpoints.PROMO_UPDATE.replace(':id', id), data);
  }

  async deletePromoCode(id) {
    return this.api.delete(config.endpoints.PROMO_DELETE.replace(':id', id));
  }

  async getPromoCodeUsage(id) {
    return this.api.get(config.endpoints.PROMO_USAGE.replace(':id', id));
  }

  // Pricing
  async getPricing() {
    return this.api.get(config.endpoints.PRICING_LIST);
  }

  async updatePricing(vehicleTypeId, data) {
    return this.api.put(config.endpoints.PRICING_UPDATE.replace(':vehicleTypeId', vehicleTypeId), data);
  }

  // Notifications
  async sendNotification(data) {
    return this.api.post(config.endpoints.NOTIFICATIONS_SEND, data);
  }

  async sendNotificationWithImage(formData) {
    return this.api.post(config.endpoints.NOTIFICATIONS_SEND, formData);
  }

  async getNotificationsHistory(params) {
    return this.api.get(config.endpoints.NOTIFICATIONS_HISTORY, { params });
  }

  // Reports
  async getReport(reportType, params) {
    return this.api.get(config.endpoints.REPORTS.replace(':reportType', reportType), { params });
  }

  // Emergency
  async getEmergencyAlerts(params) {
    return this.api.get(config.endpoints.EMERGENCY_ALERTS, { params });
  }

  async resolveEmergencyAlert(id, data) {
    return this.api.post(config.endpoints.EMERGENCY_RESOLVE.replace(':id', id), data);
  }

  // Live Tracking
  async getLiveTracking() {
    return this.api.get(config.endpoints.LIVE_TRACKING);
  }

  // Ratings
  async getRatings(params) {
    return this.api.get(config.endpoints.RATINGS_LIST, { params });
  }

  async flagRating(id, reason) {
    return this.api.post(config.endpoints.RATING_FLAG.replace(':id', id), { reason });
  }

  // Support
  async getSupportTickets(params) {
    return this.api.get(config.endpoints.SUPPORT_TICKETS, { params });
  }

  async getSupportTicketDetails(id) {
    return this.api.get(config.endpoints.SUPPORT_TICKET_DETAILS.replace(':id', id));
  }

  async replySupportTicket(id, data) {
    return this.api.post(config.endpoints.SUPPORT_TICKET_REPLY.replace(':id', id), data);
  }

  // Settings
  async getSettings() {
    return this.api.get(config.endpoints.SETTINGS_GET);
  }

  async updateSettings(data) {
    return this.api.put(config.endpoints.SETTINGS_UPDATE, data);
  }

  // Regions
  async getRegions() {
    return this.api.get(config.endpoints.REGIONS_LIST);
  }

  async addRegion(data) {
    return this.api.post(config.endpoints.REGION_ADD, data);
  }

  async updateRegion(id, data) {
    return this.api.put(config.endpoints.REGION_UPDATE.replace(':id', id), data);
  }

  async deleteRegion(id) {
    return this.api.delete(config.endpoints.REGION_DELETE.replace(':id', id));
  }

  // Document Types
  async getDocumentTypes() {
    return this.api.get(config.endpoints.DOCUMENT_TYPES_LIST);
  }

  async addDocumentType(data) {
    return this.api.post(config.endpoints.DOCUMENT_TYPE_ADD, data);
  }

  async updateDocumentType(id, data) {
    return this.api.put(config.endpoints.DOCUMENT_TYPE_UPDATE.replace(':id', id), data);
  }

  async deleteDocumentType(id) {
    return this.api.delete(config.endpoints.DOCUMENT_TYPE_DELETE.replace(':id', id));
  }

  // Main Services
  async getMainServices() {
    return this.api.get(config.endpoints.MAIN_SERVICES_LIST);
  }

  async addMainService(data) {
    return this.api.post(config.endpoints.MAIN_SERVICE_ADD, data);
  }

  async updateMainService(id, data) {
    return this.api.put(config.endpoints.MAIN_SERVICE_UPDATE.replace(':id', id), data);
  }

  async deleteMainService(id) {
    return this.api.delete(config.endpoints.MAIN_SERVICE_DELETE.replace(':id', id));
  }

  // Services
  async getServices() {
    return this.api.get(config.endpoints.SERVICES_LIST);
  }

  async addService(data) {
    return this.api.post(config.endpoints.SERVICE_ADD, data);
  }

  async updateService(id, data) {
    return this.api.put(config.endpoints.SERVICE_UPDATE.replace(':id', id), data);
  }

  async deleteService(id) {
    return this.api.delete(config.endpoints.SERVICE_DELETE.replace(':id', id));
  }

  // Complaints
  async getComplaints(params) {
    return this.api.get(config.endpoints.COMPLAINTS_LIST, { params });
  }

  async getComplaintDetails(id) {
    return this.api.get(config.endpoints.COMPLAINT_DETAILS.replace(':id', id));
  }

  async replyComplaint(id, data) {
    return this.api.post(config.endpoints.COMPLAINT_REPLY.replace(':id', id), data);
  }

  async updateComplaintStatus(id, status) {
    return this.api.patch(config.endpoints.COMPLAINT_STATUS.replace(':id', id), { status });
  }

  // Admin Users
  async getAdminUsers() {
    return this.api.get(config.endpoints.ADMIN_USERS_LIST);
  }

  async addAdminUser(data) {
    return this.api.post(config.endpoints.ADMIN_USER_ADD, data);
  }

  async updateAdminUser(id, data) {
    return this.api.put(config.endpoints.ADMIN_USER_UPDATE.replace(':id', id), data);
  }

  // Cancel Reasons
  async getCancelReasons(params) {
    return this.api.get(config.endpoints.CANCEL_REASONS_LIST, { params });
  }

  async addCancelReason(data) {
    return this.api.post(config.endpoints.CANCEL_REASON_ADD, data);
  }

  async updateCancelReason(id, data) {
    return this.api.put(config.endpoints.CANCEL_REASON_UPDATE.replace(':id', id), data);
  }

  async deleteCancelReason(id) {
    return this.api.delete(config.endpoints.CANCEL_REASON_DELETE.replace(':id', id));
  }

  // Rating Feedback Options
  async getRatingFeedbackOptions(params) {
    return this.api.get(config.endpoints.RATING_FEEDBACK_OPTIONS_LIST, { params });
  }

  async addRatingFeedbackOption(data) {
    return this.api.post(config.endpoints.RATING_FEEDBACK_OPTION_ADD, data);
  }

  async updateRatingFeedbackOption(id, data) {
    return this.api.put(config.endpoints.RATING_FEEDBACK_OPTION_UPDATE.replace(':id', id), data);
  }

  async deleteRatingFeedbackOption(id) {
    return this.api.delete(config.endpoints.RATING_FEEDBACK_OPTION_DELETE.replace(':id', id));
  }

  // FAQs
  async getFAQs(params) {
    return this.api.get(config.endpoints.FAQS_LIST, { params });
  }

  async addFAQ(data) {
    return this.api.post(config.endpoints.FAQ_ADD, data);
  }

  async updateFAQ(id, data) {
    return this.api.put(config.endpoints.FAQ_UPDATE.replace(':id', id), data);
  }

  async deleteFAQ(id) {
    return this.api.delete(config.endpoints.FAQ_DELETE.replace(':id', id));
  }

  // Legal Pages
  async getLegalPages(params) {
    return this.api.get(config.endpoints.LEGAL_PAGES_LIST, { params });
  }

  async getLegalPage(pageType, userType) {
    return this.api.get(
      config.endpoints.LEGAL_PAGE_GET
        .replace(':pageType', pageType)
        .replace(':userType', userType)
    );
  }

  async updateLegalPage(pageType, userType, data) {
    return this.api.put(
      config.endpoints.LEGAL_PAGE_UPDATE
        .replace(':pageType', pageType)
        .replace(':userType', userType),
      data
    );
  }

  // ==========================================
  // Recharge Plans
  // ==========================================

  async getRechargePlans(params = {}) {
    return this.api.get('/admin/recharge-plans', { params });
  }

  async createRechargePlan(data) {
    return this.api.post('/admin/recharge-plans', data);
  }

  async updateRechargePlan(id, data) {
    return this.api.put(`/admin/recharge-plans/${id}`, data);
  }

  async deleteRechargePlan(id) {
    return this.api.delete(`/admin/recharge-plans/${id}`);
  }

  // ==========================================
  // Notification History
  // ==========================================

  async getNotificationHistory(params = {}) {
    return this.api.get('/admin/notifications/history', { params });
  }

  async deleteNotification(id) {
    return this.api.delete(`/admin/notifications/${id}`);
  }

  // ==========================================
  // Emergency Contacts
  // ==========================================

  async getEmergencyContacts(params = {}) {
    return this.api.get('/admin/emergency-contacts', { params });
  }

  async addEmergencyContact(data) {
    return this.api.post('/admin/emergency-contacts', data);
  }

  async updateEmergencyContact(id, data) {
    return this.api.put(`/admin/emergency-contacts/${id}`, data);
  }

  async deleteEmergencyContact(id) {
    return this.api.delete(`/admin/emergency-contacts/${id}`);
  }

  async searchPassengers(search) {
    return this.api.get('/admin/passengers/search', { params: { search } });
  }

  // ==========================================
  // Quick Chat Messages
  // ==========================================

  async getQuickChatMessages(params = {}) {
    return this.api.get('/admin/quick-chat', { params });
  }

  async addQuickChatMessage(data) {
    return this.api.post('/admin/quick-chat', data);
  }

  async updateQuickChatMessage(id, data) {
    return this.api.put(`/admin/quick-chat/${id}`, data);
  }

  async deleteQuickChatMessage(id) {
    return this.api.delete(`/admin/quick-chat/${id}`);
  }

  // ==========================================
  // Chat Conversations
  // ==========================================

  async getChatConversations(params = {}) {
    return this.api.get('/admin/chat-conversations', { params });
  }

  async getRideChatMessages(rideId) {
    return this.api.get(`/admin/chat-conversations/${rideId}`);
  }

  async deleteChatConversation(rideId) {
    return this.api.delete(`/admin/chat-conversations/${rideId}`);
  }

  // ==========================================
  // Database Backup
  // ==========================================

  async getBackupSettings() {
    return this.api.get('/admin/backup/settings');
  }

  async updateBackupSettings(data) {
    return this.api.post('/admin/backup/settings', data);
  }

  async executeBackup() {
    return this.api.post('/admin/backup/execute');
  }

  async getBackupHistory() {
    return this.api.get('/admin/backup/history');
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
